import React, { useState } from 'react';
import { AlertCircle, Info, TrendingUp, TrendingDown, Zap } from 'lucide-react';
import PatientContext from './PatientContext.tsx';

interface Alert {
  id: string;
  patient_id?: string;
  lab_name: string;
  lab_value: number;
  reference_range: string;
  patient_name: string;
  mrn: string;
  classification: 'Actionable' | 'FYI';
  reasoning: string;
  trend: 'stable' | 'up' | 'down';
  model_used: 'claude' | 'mistral';
  confidence: number;
  timestamp: string;
}

interface AlertListProps {
  alerts: Alert[];
}

const AlertList: React.FC<AlertListProps> = ({ alerts }) => {
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [filter, setFilter] = useState<'all' | 'actionable' | 'fyi'>('all');
  const [selectedPatientId, setSelectedPatientId] = useState<string | null>(null);

  const filtered = alerts.filter(a => {
    if (filter === 'actionable') return a.classification === 'Actionable';
    if (filter === 'fyi') return a.classification === 'FYI';
    return true;
  });

  const getTrendIcon = (trend: string) => {
    if (trend === 'up') return <TrendingUp className="w-4 h-4 text-red-500" />;
    if (trend === 'down') return <TrendingDown className="w-4 h-4 text-blue-500" />;
    return <div className="w-4 h-4 text-gray-400">−</div>;
  };

  return (
    <div className="space-y-4">
      {/* Filter Buttons */}
      <div className="flex gap-2 mb-6">
        <button
          onClick={() => setFilter('all')}
          className={`px-4 py-2 rounded-lg font-medium transition ${
            filter === 'all'
              ? 'bg-gray-900 text-white'
              : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
          }`}
        >
          All ({alerts.length})
        </button>
        <button
          onClick={() => setFilter('actionable')}
          className={`px-4 py-2 rounded-lg font-medium transition ${
            filter === 'actionable'
              ? 'bg-red-600 text-white'
              : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
          }`}
        >
          Actionable ({alerts.filter(a => a.classification === 'Actionable').length})
        </button>
        <button
          onClick={() => setFilter('fyi')}
          className={`px-4 py-2 rounded-lg font-medium transition ${
            filter === 'fyi'
              ? 'bg-blue-600 text-white'
              : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
          }`}
        >
          FYI ({alerts.filter(a => a.classification === 'FYI').length})
        </button>
      </div>

      {/* Alert Cards */}
      <div className="space-y-3">
        {filtered.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            No {filter !== 'all' ? filter : ''} alerts
          </div>
        ) : (
          filtered.map(alert => (
            <div
              key={alert.id}
              className={`border rounded-lg p-4 cursor-pointer transition ${
                alert.classification === 'Actionable'
                  ? 'border-red-300 bg-red-50 hover:bg-red-100'
                  : 'border-blue-200 bg-blue-50 hover:bg-blue-100'
              }`}
              onClick={() => setExpandedId(expandedId === alert.id ? null : alert.id)}
            >
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-4 flex-1">
                  {/* Icon */}
                  <div className="flex-shrink-0 pt-1">
                    {alert.classification === 'Actionable' ? (
                      <AlertCircle className="w-5 h-5 text-red-600" />
                    ) : (
                      <Info className="w-5 h-5 text-blue-600" />
                    )}
                  </div>

                  {/* Content */}
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-semibold text-gray-900">
                        {alert.patient_name} • {alert.lab_name}
                      </h3>
                      {getTrendIcon(alert.trend)}
                    </div>
                    <p className="text-sm text-gray-600 mb-2">
                      MRN: {alert.mrn} • Value: <span className="font-mono font-bold">{alert.lab_value}</span> ({alert.reference_range})
                    </p>

                    {/* Badge and Meta */}
                    <div className="flex items-center gap-2">
                      <span
                        className={`px-2 py-1 text-xs font-medium rounded ${
                          alert.classification === 'Actionable'
                            ? 'bg-red-200 text-red-900'
                            : 'bg-blue-200 text-blue-900'
                        }`}
                      >
                        {alert.classification}
                      </span>
                      <span className="text-xs text-gray-500">
                        {alert.model_used === 'claude' ? (
                          <span>🧠 Claude • {(alert.confidence * 100).toFixed(0)}%</span>
                        ) : (
                          <span>⚡ Mistral • {(alert.confidence * 100).toFixed(0)}%</span>
                        )}
                      </span>
                      <span className="text-xs text-gray-400">
                        {new Date(alert.timestamp).toLocaleTimeString()}
                      </span>
                    </div>

                    {/* Preview of reasoning */}
                    {expandedId !== alert.id && (
                      <p className="text-sm text-gray-700 mt-2 line-clamp-2">{alert.reasoning}</p>
                    )}
                  </div>
                </div>

                {/* Expand indicator */}
                <div className="flex-shrink-0 text-gray-400">
                  <Zap className={`w-5 h-5 transition ${expandedId === alert.id ? 'rotate-90' : ''}`} />
                </div>
              </div>

              {/* Expanded Reasoning */}
              {expandedId === alert.id && (
                <div className="mt-4 pt-4 border-t border-current border-opacity-20">
                  <div className="bg-white bg-opacity-50 rounded p-3">
                    <h4 className="text-sm font-semibold text-gray-900 mb-2">AI Reasoning:</h4>
                    <p className="text-sm text-gray-700 leading-relaxed">{alert.reasoning}</p>

                    {/* Action Buttons */}
                    <div className="flex gap-2 mt-4">
                      <button
                        onClick={() => setSelectedPatientId(alert.patient_id || alert.mrn)}
                        className="px-3 py-1.5 bg-white border border-gray-300 rounded text-sm font-medium text-gray-700 hover:bg-gray-50"
                      >
                        View Patient
                      </button>
                      <button className="px-3 py-1.5 bg-white border border-gray-300 rounded text-sm font-medium text-gray-700 hover:bg-gray-50">
                        View Trend
                      </button>
                      <button
                        className={`px-3 py-1.5 rounded text-sm font-medium text-white ${
                          alert.classification === 'Actionable'
                            ? 'bg-red-600 hover:bg-red-700'
                            : 'bg-blue-600 hover:bg-blue-700'
                        }`}
                      >
                        {alert.classification === 'Actionable' ? 'Review & Act' : 'Acknowledge'}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {/* Patient Context Modal */}
      {selectedPatientId && (
        <PatientContext
          patientId={selectedPatientId}
          onClose={() => setSelectedPatientId(null)}
        />
      )}
    </div>
  );
};

export default AlertList;
